// @flow
import React, {Component} from 'react';
import {isEqual, pick} from 'lodash';
import {compose} from 'redux';
import {connect} from 'react-redux';
import searchQuery, {
    FORCE_GRAPHQL_ERROR_HEADER,
    searchQueryVariables
} from '../../queries/searchQuery';
import searchRequestQuery, {
    searchRequestQueryVariables
} from '../../queries/searchRequestQuery';
import {ResultsParams} from '../../../server/graphql/params';
import searchQueryActions from '../../actions/searchQuery';

import type {ComponentType} from 'react';
import type {SearchQueryResponseData} from '../../../../src/universal/queries/searchQuery';
import type {State as ReduxState} from '../../reducers/index';
import type {SearchParamsType} from '../../reducers/searchParams';
import withQuery from '../withQuery/withQuery';

type DispatchProps = {
    searchQueryComplete: typeof searchQueryActions.searchQueryComplete,
    searchQueryStart: typeof searchQueryActions.searchQueryStart
};

type StateProps = SearchParamsType & {
    pathname: string,
    gtsQuery: boolean
};

type ConnectorProps = DispatchProps & StateProps;

type WrapperProps = ConnectorProps & {
    data: SearchQueryResponseData
};

const encodedQuery = encodeURIComponent(searchQuery.loc.source.body);
const encodedSearchRequestQuery = encodeURIComponent(
    searchRequestQuery.loc.source.body
);

export const queryConfig = (props: ConnectorProps) => ({
    document: props.gtsQuery ? searchRequestQuery : searchQuery,
    options: {
        errorPolicy: 'all',
        context: {
            headers: {
                [FORCE_GRAPHQL_ERROR_HEADER]:
                    typeof window !== 'undefined' && window.forceGraphQLError
            }
        },
        variables: props.gtsQuery
            ? searchRequestQueryVariables(props)
            : searchQueryVariables(props, props.pathname),
        ssr: false // don't need to ssr here, since we already fetched in the application route handler,
    }
});

export default function withSearchQuery<OwnProps: {}>(
    WrappedComponent: ComponentType<$Supertype<OwnProps>>
): ComponentType<OwnProps> {
    type Props = OwnProps & WrapperProps;

    class WithSearchQuery extends Component<Props> {
        componentDidMount() {
            const {data, searchQueryComplete} = this.props;
            if (!data.loading) {
                searchQueryComplete(data);
            }
        }

        shouldComponentUpdate(nextProps: Props) {
            const {loading, results, error} = this.props.data;
            const nextLoading = nextProps.data.loading;
            const nextResults = nextProps.data.results;
            const nextError = nextProps.data.error;

            return (
                loading !== nextLoading ||
                !isEqual(results, nextResults) ||
                !isEqual(error, nextError)
            );
        }

        componentDidUpdate(prevProps: Props) {
            const {loading: prevLoading} = prevProps.data;
            const {data, searchQueryComplete, searchQueryStart} = this.props;
            // we need only check loading states since shouldComponentUpdate
            // guarantees that app has either moved to a loading state
            // or results have changed

            // if !data.loading and !prevLoading, then the cache was used to retrieve data
            // This will guarantee that corresponding dispatches to searchQueryStart
            // always precede those to searchQueryComplete
            if (data.loading) {
                searchQueryStart(data);
            } else if (prevLoading && !data.loading) {
                searchQueryComplete(data);
            } else {
                searchQueryStart(data);
                searchQueryComplete(data);
            }
        }

        encodeVariables = () => {
            const {variables} = this.props.data;
            return encodeURIComponent(JSON.stringify(variables));
        };

        render() {
            const graphiqlUrl = `/graphiql?operationName=SearchQuery&query=${
                this.props.gtsQuery ? encodedSearchRequestQuery : encodedQuery
            }&variables=${this.encodeVariables()}`;
            return (
                <WrappedComponent {...this.props} graphiqlUrl={graphiqlUrl} />
            );
        }
    }

    const mapStateToGraphlProps = ({
        system: {gtsQuery},
        searchParams
    }: ReduxState): StateProps => ({
        gtsQuery,
        pathname: searchParams.pathname,
        ...pick(searchParams.params, ResultsParams)
    });

    const mapDispatchToProps = {
        searchQueryStart: searchQueryActions.searchQueryStart,
        searchQueryComplete: searchQueryActions.searchQueryComplete
    };

    return compose(
        connect(
            mapStateToGraphlProps,
            mapDispatchToProps
        ),
        withQuery(queryConfig)
    )(WithSearchQuery);
}
